import React, { useState } from 'react';
import { Box, Button, Chip, Collapse, IconButton, Menu, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { AccountBalanceWalletOutlined, CardGiftcard, EmojiEvents, ExpandLess, ExpandMore, Inventory2, MoreVert, NoteAlt, Storefront } from '@mui/icons-material';
import DenominationBreakdown from '../../components/pos/DenominationBreakdown';
import { cartSummaryText } from '../../utils/cartSummary';

// Mobile action-button order (desktop grid keeps its own order).
const MOBILE_BTN_ORDER = ['Customer', 'Catalog', 'Sales Person', 'Discount', 'Hold Bill', 'Carry Bag', 'Held', 'Promotions'];
const btnRank = (label) => {
  const key = label === 'Close' ? 'Catalog' : label; // Catalog toggles its label to "Close"
  const i = MOBILE_BTN_ORDER.indexOf(key);
  return i === -1 ? 99 : i;
};

export default function DashboardMobileControls(props) {
  const {
    billingMode,
    inExchangeWorkflow,
    actionButtons,
    currentSession,
    setIsCashMovementModalOpen,
    onOpenCashOffice,
    cashInOutEnabled = true,
    cashOfficeIncoming,
    setIsCloseSessionModalOpen,
    setIsOpenSessionModalOpen,
    orderNotes,
    setOrderNotes,
    handleNotesBlur,
    exchangeReturnTotal,
    exchangeNewTotal,
    gross_subtotal,
    subtotal,
    discount_total,
    tax_total,
    orderCartItems,
    billExpanded,
    denomBreakdownExpanded,
    setDenomBreakdownExpanded,
    bill_discount_id,
    bill_discount_name,
    bill_discount_amount,
    currentOrderId,
    exchangeDirection,
    exchangeReturnItemsPayload,
    exchangeNewItems,
    cart,
    store,
    exchangeNetAmount,
    displayTotal,
    setBillExpanded,
    handleCancelExchange,
    canCompleteExchange,
    exchangeBlockReason,
    sessionBlocked,
    setExchangeCheckoutOpen,
    handleDiscard,
    customerRequired,
    salespersonRequired,
    selectedCustomer,
    selectedSalesperson,
    checkoutBlocked,
    setIsCheckoutOpen,
    onOpenStockLookup,
    inStockOnly,
    setInStockOnly,
    giftVoucherSlots,
    onOpenBalanceCheck,
    onOpenIncentives,
  } = props;

  const [noteOpen, setNoteOpen] = useState(false);
  const [sessionMenuEl, setSessionMenuEl] = useState(null);

  if (!(billingMode === 'billing' || billingMode === 'gift_voucher' || inExchangeWorkflow)) return null;

  const gross = parseFloat(gross_subtotal || 0);
  const sub = parseFloat(subtotal || 0);
  const disc = parseFloat(discount_total || 0);
  const tax = parseFloat(tax_total || 0);
  const totalCgst = orderCartItems.reduce((s, i) => s + parseFloat(i.cgst_amount || 0), 0);
  const totalSgst = orderCartItems.reduce((s, i) => s + parseFloat(i.sgst_amount || 0), 0);
  const totalIgst = orderCartItems.reduce((s, i) => s + parseFloat(i.igst_amount || 0), 0);
  const isIntra = totalCgst > 0 || totalSgst > 0;
  const hasDenomBreakdown = (currentSession?.opening_denominations?.length > 0) || (currentSession?.closing_denominations?.length > 0);

  const MRow = ({ label, value, color }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.25 }}>
      <Typography sx={{ fontSize: '0.68rem', color: color || '#94A3B8' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: color || '#64748B' }}>₹{parseFloat(value || 0).toFixed(2)}</Typography>
    </Box>
  );

  return (
    <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', bgcolor: '#FFF', borderTop: '2px solid #E2E8F0', flexShrink: 0 }}>
      <Box sx={{ display: 'flex', overflowX: 'auto', gap: 0.6, px: 1.5, py: 0.75, borderBottom: '1px solid #F1F5F9', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
        {[...actionButtons].sort((a, b) => btnRank(a.label) - btnRank(b.label)).map((btn, i) => (
          <Button key={i} variant="outlined" size="small" onClick={btn.onClick} disabled={btn.disabled} startIcon={<Box sx={{ color: btn.color, display: 'flex', '& svg': { fontSize: '14px !important' } }}>{btn.icon}</Box>} sx={{ flexShrink: 0, height: 34, borderColor: '#E2E8F0', color: '#1E293B', textTransform: 'none', fontWeight: 700, fontSize: '0.7rem', px: 1.1, borderRadius: '8px', whiteSpace: 'nowrap', bgcolor: '#FFF', '&:hover': { bgcolor: '#F1F5F9', borderColor: btn.color }, gap: 0.4 }}>
            {btn.label}
          </Button>
        ))}
      </Box>

      {giftVoucherSlots?.approvals && (
        <Box sx={{ px: 1.5, py: 0.6, borderBottom: '1px solid #F1F5F9' }}>{giftVoucherSlots.approvals}</Box>
      )}
      {/* Utility row: session (left, sized to content) + note/stock-lookup/in-stock icons (right, filling the rest) */}
      <Box sx={{ px: 1.5, py: 0.6, borderBottom: '1px solid #F1F5F9', display: 'grid', gridTemplateColumns: 'auto 1fr', alignItems: 'center', gap: 1.5 }}>
        {/* Session — sized to its own content */}
        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {currentSession && (
            <>
              <Chip label={currentSession.status.toUpperCase()} size="small" sx={{ height: 18, fontSize: '0.56rem', fontWeight: 800, flexShrink: 0, bgcolor: currentSession.status === 'open' ? '#D1FAE5' : '#FEF3C7', color: currentSession.status === 'open' ? '#065F46' : '#92400E', borderRadius: '4px' }} />
              <Typography sx={{ fontSize: '0.62rem', color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentSession.session_number}</Typography>
              <IconButton size="small" onClick={(e) => setSessionMenuEl(e.currentTarget)} sx={{ p: 0.2, color: '#94A3B8', flexShrink: 0, '&:hover': { color: '#4F46E5' } }}>
                <MoreVert sx={{ fontSize: 16 }} />
              </IconButton>
              <Menu
                anchorEl={sessionMenuEl}
                open={Boolean(sessionMenuEl)}
                onClose={() => setSessionMenuEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                {currentSession.status === 'open'
                  ? [
                      ...(cashInOutEnabled ? [<MenuItem key="cash" onClick={() => { setSessionMenuEl(null); setIsCashMovementModalOpen(true); }} sx={{ fontSize: '0.8rem' }}>Cash Entry</MenuItem>] : []),
                      ...(onOpenCashOffice ? [<MenuItem key="cash-office" onClick={() => { setSessionMenuEl(null); onOpenCashOffice(); }} sx={{ fontSize: '0.8rem' }}>Cash Office</MenuItem>] : []),
                      <MenuItem key="close" onClick={() => { setSessionMenuEl(null); setIsCloseSessionModalOpen(true); }} sx={{ fontSize: '0.8rem', color: '#EF4444' }}>Close Session</MenuItem>,
                    ]
                  : <MenuItem onClick={() => { setSessionMenuEl(null); setIsOpenSessionModalOpen(true); }} sx={{ fontSize: '0.8rem' }}>Open Session</MenuItem>}
              </Menu>
            </>
          )}
        </Box>

        {/* Note / stock lookup / in-stock toggle — fills the remaining width, right-aligned with room to breathe */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.25, mr: 2 }}>
          <Tooltip title={orderNotes ? 'Note added — tap to edit' : 'Add order note'}>
            <IconButton
              size="small"
              onClick={() => setNoteOpen((v) => !v)}
              sx={{
                height: 34, width: 34, borderRadius: '8px', border: '1px solid',
                color: orderNotes ? '#4F46E5' : '#64748B',
                borderColor: orderNotes ? '#C7D2FE' : '#E2E8F0',
                bgcolor: orderNotes ? '#EEF2FF' : '#FFF',
                '&:hover': { bgcolor: '#EEF2FF', borderColor: '#C7D2FE' },
              }}
            >
              <NoteAlt sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>

          {onOpenCashOffice && (
            <Tooltip title={cashOfficeIncoming ? `Cash Office · ${cashOfficeIncoming} on the way` : 'Cash Office'}>
              <IconButton
                size="small"
                aria-label="Cash Office"
                onClick={onOpenCashOffice}
                sx={{ position: 'relative', height: 34, width: 34, borderRadius: '8px', border: '1px solid #C7D2FE', color: '#4F46E5', bgcolor: '#EEF2FF', '&:hover': { bgcolor: '#EEF2FF', borderColor: '#4F46E5' } }}
              >
                <AccountBalanceWalletOutlined sx={{ fontSize: 17 }} />
                {cashOfficeIncoming > 0 && (
                  <Box component="span" sx={{ position: 'absolute', top: -5, right: -5, bgcolor: '#4F46E5', color: '#fff', fontSize: '0.55rem', fontWeight: 800, borderRadius: '8px', px: 0.5, lineHeight: '14px' }}>
                    {cashOfficeIncoming}
                  </Box>
                )}
              </IconButton>
            </Tooltip>
          )}

          {onOpenBalanceCheck && (
            <Tooltip title="Check gift voucher balance">
              <IconButton
                size="small"
                aria-label="Check gift voucher balance"
                onClick={onOpenBalanceCheck}
                sx={{ height: 34, width: 34, borderRadius: '8px', border: '1px solid #E2E8F0', color: '#DB2777', bgcolor: '#FFF', '&:hover': { bgcolor: '#FDF2F8' } }}
              >
                <CardGiftcard sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
          {onOpenIncentives && (
            <Tooltip title="Incentives leaderboard">
              <IconButton
                size="small"
                aria-label="Incentives leaderboard"
                onClick={onOpenIncentives}
                sx={{ height: 34, width: 34, borderRadius: '8px', border: '1px solid #E2E8F0', color: '#4F46E5', bgcolor: '#FFF', '&:hover': { bgcolor: '#EEF2FF' } }}
              >
                <EmojiEvents sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
          {(billingMode === 'billing' || inExchangeWorkflow) && (
            <Tooltip title="Check stock at other stores &amp; warehouses">
              <IconButton
                size="small"
                onClick={() => onOpenStockLookup?.()}
                sx={{ height: 34, width: 34, borderRadius: '8px', border: '1px solid #E2E8F0', color: '#4F46E5', bgcolor: '#FFF', '&:hover': { bgcolor: '#EEF2FF' } }}
              >
                <Storefront sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}

          {(billingMode === 'billing' || inExchangeWorkflow) && (
            <Tooltip title={inStockOnly ? 'Showing in-stock-here only — tap to show all' : 'Show only products in stock at this store'}>
              <IconButton
                size="small"
                onClick={() => setInStockOnly?.((prev) => !prev)}
                sx={{
                  height: 34, width: 34, borderRadius: '8px', border: '1px solid',
                  color: inStockOnly ? '#4F46E5' : '#64748B',
                  borderColor: inStockOnly ? '#C7D2FE' : '#E2E8F0',
                  bgcolor: inStockOnly ? '#EEF2FF' : '#FFF',
                  '&:hover': { bgcolor: '#EEF2FF', borderColor: '#C7D2FE' },
                }}
              >
                <Inventory2 sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Collapsible order note field */}
      <Collapse in={noteOpen}>
        <Box sx={{ px: 1.5, pb: 0.5, pt: 0.25 }}>
          <TextField fullWidth multiline minRows={1} maxRows={2} autoFocus placeholder={inExchangeWorkflow ? 'Add exchange note...' : 'Add order note...'} value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} onBlur={handleNotesBlur} InputProps={{ startAdornment: <NoteAlt sx={{ fontSize: 14, color: '#94A3B8', mr: 0.6, alignSelf: 'flex-start', mt: '5px' }} /> }} sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.72rem', borderRadius: '8px', bgcolor: '#FFF', '& fieldset': { borderColor: '#E2E8F0' }, '&:hover fieldset': { borderColor: '#CBD5E1' }, '&.Mui-focused fieldset': { borderColor: '#4F46E5' } }, '& .MuiOutlinedInput-input': { py: 0.35, px: 0.6 } }} />
        </Box>
      </Collapse>

      {currentSession && hasDenomBreakdown && (
        <Box sx={{ px: 1.5, py: 0.6, borderBottom: '1px solid #F1F5F9' }}>
          <DenominationBreakdown
            openingDenominations={currentSession.opening_denominations}
            closingDenominations={currentSession.closing_denominations}
            expanded={denomBreakdownExpanded}
            onToggle={() => setDenomBreakdownExpanded((v) => !v)}
          />
        </Box>
      )}

      <Box sx={{ bgcolor: '#F8FAFC', borderTop: '1px solid #F1F5F9' }}>
        {giftVoucherSlots ? giftVoucherSlots.total : (
        <>
        {inExchangeWorkflow && (
          <Box sx={{ pt: 0.75, pb: 0.25, borderBottom: '1px dashed #E2E8F0' }}>
            {[{ label: 'Return Credit', value: exchangeReturnTotal, color: '#C2410C', prefix: '−' }, { label: 'New Items', value: exchangeNewTotal, color: '#4F46E5', prefix: '+' }].map(({ label, value, color, prefix }) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.3 }}>
                <Typography sx={{ fontSize: '0.7rem', color }}>{label}</Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color }}>{prefix}₹{value.toFixed(2)}</Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ px: 1.5, py: 0.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ minWidth: 0, mr: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 900, color: inExchangeWorkflow ? (exchangeDirection === 'customer_pays' ? '#1D4ED8' : exchangeDirection === 'customer_refund' ? '#7C3AED' : '#059669') : '#94A3B8', letterSpacing: '0.04rem' }}>
                {inExchangeWorkflow ? (exchangeDirection === 'customer_pays' ? 'CUSTOMER PAYS' : exchangeDirection === 'customer_refund' ? 'CUSTOMER REFUND' : 'EVEN EXCHANGE') : 'TOTAL DUE (INCL. TAX)'}
              </Typography>
              {!inExchangeWorkflow && currentOrderId && <IconButton size="small" onClick={() => setBillExpanded((v) => !v)} sx={{ p: 0.15, color: '#94A3B8', '&:hover': { color: '#4F46E5' } }}>{billExpanded ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}</IconButton>}
            </Box>
            <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 700 }}>{inExchangeWorkflow ? `${exchangeReturnItemsPayload.length} return item${exchangeReturnItemsPayload.length !== 1 ? 's' : ''} · ${exchangeNewItems.length} new item${exchangeNewItems.length !== 1 ? 's' : ''}` : `${cartSummaryText(cart)} · ${store?.name || 'Store'}`}</Typography>
          </Box>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 900, color: inExchangeWorkflow ? (exchangeDirection === 'customer_pays' ? '#1D4ED8' : exchangeDirection === 'customer_refund' ? '#7C3AED' : '#059669') : '#4F46E5', lineHeight: 1, flexShrink: 0 }}>
            {inExchangeWorkflow ? (exchangeDirection === 'even' ? '₹0' : `₹${Math.abs(exchangeNetAmount).toFixed(2)}`) : `₹${displayTotal.toFixed(2)}`}
          </Typography>
        </Box>

        {/* Subtotal / tax breakdown — opens BELOW the Total Due row, collapsed by default */}
        {!inExchangeWorkflow && billExpanded && (
          <Box sx={{ pt: 0.4, pb: 0.5, borderTop: '1px dashed #E2E8F0' }}>
            <MRow label="Subtotal" value={gross} />
            {disc > 0 && <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.25 }}><Typography sx={{ fontSize: '0.68rem', color: '#10B981' }}>Discount</Typography><Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#10B981' }}>−₹{disc.toFixed(2)}</Typography></Box>}
            {bill_discount_id && <Box sx={{ pl: 3, pr: 1.5, borderLeft: '2px solid #D1FAE5', ml: 1.5 }}><Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}><Typography sx={{ fontSize: '0.62rem', color: '#6EE7B7' }}>↳ {bill_discount_name}</Typography><Typography sx={{ fontSize: '0.62rem', color: '#10B981' }}>−₹{parseFloat(bill_discount_amount || 0).toFixed(2)}</Typography></Box></Box>}
            <MRow label="Taxable Amount" value={sub} />
            <MRow label="Tax (GST)" value={tax} />
            {currentOrderId && tax > 0 && <Box sx={{ pl: 3, pr: 1.5, borderLeft: '2px solid #EEF2FF', ml: 1.5 }}>{isIntra ? <><Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}><Typography sx={{ fontSize: '0.62rem', color: '#CBD5E1' }}>↳ CGST</Typography><Typography sx={{ fontSize: '0.62rem', color: '#94A3B8' }}>₹{totalCgst.toFixed(2)}</Typography></Box><Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}><Typography sx={{ fontSize: '0.62rem', color: '#CBD5E1' }}>↳ SGST</Typography><Typography sx={{ fontSize: '0.62rem', color: '#94A3B8' }}>₹{totalSgst.toFixed(2)}</Typography></Box></> : <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}><Typography sx={{ fontSize: '0.62rem', color: '#CBD5E1' }}>↳ IGST</Typography><Typography sx={{ fontSize: '0.62rem', color: '#94A3B8' }}>₹{(totalIgst || tax).toFixed(2)}</Typography></Box>}</Box>}
          </Box>
        )}
        </>
        )}
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        {giftVoucherSlots ? giftVoucherSlots.buttons : inExchangeWorkflow ? (
          <>
            <Button variant="outlined" onClick={handleCancelExchange} sx={{ height: 52, color: '#64748B', borderColor: '#E2E8F0', fontWeight: 800, borderRadius: '8px', fontSize: '0.8rem', '&:hover': { bgcolor: '#F8FAFC' } }}>CANCEL</Button>
            <Tooltip title={exchangeBlockReason} placement="top" arrow><span><Button variant="contained" disableElevation disabled={!canCompleteExchange || sessionBlocked} onClick={() => setExchangeCheckoutOpen(true)} sx={{ height: 52, bgcolor: '#6366F1', fontWeight: 900, borderRadius: '8px', fontSize: '0.88rem', letterSpacing: '0.02rem', '&:hover': { bgcolor: '#4F46E5' }, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' }, width: '100%' }}>COMPLETE EXCHANGE</Button></span></Tooltip>
          </>
        ) : (
          <>
            <Button variant="outlined" onClick={handleDiscard} disabled={billingMode === 'return'} sx={{ height: 52, color: '#EF4444', borderColor: '#EF4444', fontWeight: 800, borderRadius: '8px', fontSize: '0.8rem', '&:hover': { bgcolor: '#FEF2F2' } }}>DISCARD</Button>
            <Tooltip title={sessionBlocked && cart.length > 0 ? 'Open a cash session to continue billing' : customerRequired && !selectedCustomer && cart.length > 0 ? 'Customer required — tap F2 to add' : salespersonRequired && !selectedSalesperson && cart.length > 0 ? 'Salesperson required — tap F6 to add' : ''} placement="top" arrow><span><Button variant="contained" disableElevation disabled={checkoutBlocked || billingMode === 'return'} onClick={() => setIsCheckoutOpen(true)} sx={{ height: 52, bgcolor: '#4F46E5', fontWeight: 900, borderRadius: '8px', fontSize: '1rem', letterSpacing: '0.02rem', '&:hover': { bgcolor: '#4338CA' }, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' }, width: '100%' }}>CHECKOUT</Button></span></Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}
